import { keyframes, styled } from '@mui/system';
import { Props } from './types';

type T = Pick<Props, 'palette' | 'size' | 'variant' | 'flow' | 'borderRadius'>;

export const SIZES = {
  // XXSMALL: 20,
  XSMALL: 30,
  SMALL: 40,
  MEDIUM: 50
  // LARGE: 60,
  // XLARGE: 70,
  // XXLARGE: 80,
};

export const Root = styled('div')<T>`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: 1fr;
  align-items: center;
  overflow: hidden;

  height: ${({ size, variant }) => {
    switch (variant) {
      case 'link': {
        return 'auto';
      }

      default: {
        switch (size) {
          case 'xsmall': {
            return `${SIZES.XSMALL}px`;
          }
          case 'small': {
            return `${SIZES.SMALL}px`;
          }
          default: {
            return `${SIZES.MEDIUM}px`;
          }
        }
      }
    }
  }};

  min-width: ${({ flow, size }) => {
    switch (flow) {
      case 'square': {
        switch (size) {
          case 'xsmall': {
            return `${SIZES.XSMALL}px`;
          }
          case 'small': {
            return `${SIZES.SMALL}px`;
          }
          default: {
            return `${SIZES.MEDIUM}px`;
          }
        }
      }
      case 'fluid': {
        return 'unset';
      }
      default: {
        return '80px';
      }
    }
  }};

  width: ${({ flow, size }) => {
    switch (flow) {
      case 'square': {
        switch (size) {
          case 'xsmall': {
            return `${SIZES.XSMALL}px`;
          }
          case 'small': {
            return `${SIZES.SMALL}px`;
          }
          default: {
            return `${SIZES.MEDIUM}px`;
          }
        }
      }
      case 'fluid': {
        return '100%';
      }
      default: {
        return 'initial';
      }
    }
  }};

  color: ${({ palette, variant }) => {
    switch (variant) {
      case 'transparent': {
        switch (palette) {
          case 'primary': {
            return 'var(color-secondary)';
          }
          case 'secondary': {
            return '#fff';
          }
          default: {
            return 'var(color-primary)';
          }
        }
      }

      case 'ghost':
      case 'soft':
      case 'link': {
        switch (palette) {
          case 'primary': {
            return 'var(color-secondary)';
          }
          default: {
            return 'var(color-primary)';
          }
        }
      }

      // case "solid"
      default: {
        return '#fff';
      }
    }
  }};

  border-style: solid;

  border-radius: ${({ borderRadius }) => {
    switch (borderRadius) {
      case 'large': {
        return '15px';
      }
      case 'medium': {
        return '10px';
      }
      case 'small': {
        return '5px';
      }
      case 'none': {
        return '0px';
      }
      default: {
        return '4px';
      }
    }
  }};

  border-width: ${({ variant, size }) => {
    switch (variant) {
      case 'ghost': {
        switch (size) {
          case 'xsmall': {
            return '1px';
          }
          case 'small': {
            return '1px';
          }
          default: {
            return '2px';
          }
        }
      }

      default: {
        return '0';
      }
    }
  }};

  border-color: ${({ palette, variant }) => {
    switch (variant) {
      case 'ghost': {
        switch (palette) {
          case 'primary': {
            return 'var(color-secondary)';
          }
          default: {
            return 'var(color-primary)';
          }
        }
      }

      case 'soft': {
        switch (palette) {
          case 'primary': {
            return 'var(color-secondary)';
          }
          default: {
            return 'var(color-primary)';
          }
        }
      }

      // case "transparent"
      // case "solid"
      // case "link"
      default: {
        return 'transparent';
      }
    }
  }};

  --background: ${({ palette, variant }) => {
    switch (variant) {
      case 'transparent':
      case 'ghost':
      case 'link': {
        return 'transparent';
      }

      case 'soft': {
        switch (palette) {
          case 'primary': {
            // TODO: add opacity
            return 'rgb(234 71 92 / .2)';
          }
          default: {
            // TODO: add opacity
            return 'rgb(41 41 41 / .2)';
          }
        }
      }

      // case "solid"
      default: {
        switch (palette) {
          case 'primary': {
            return 'var(--color-secondary)';
          }
          default: {
            return 'var(--color-primary)';
          }
        }
      }
    }
  }};
`;

export const ButtonStyled = styled('button')<T>`
  color: inherit;
  height: inherit;
  background: var(--background);

  width: ${({ flow, size }) => {
    switch (flow) {
      case 'square': {
        switch (size) {
          case 'xsmall': {
            return `${SIZES.XSMALL}px`;
          }
          case 'small': {
            return `${SIZES.SMALL}px`;
          }
          default: {
            return `${SIZES.MEDIUM}px`;
          }
        }
      }
      case 'fluid': {
        return '100%';
      }
      default: {
        return 'initial';
      }
    }
  }};

  text-decoration: ${({ variant }) => {
    switch (variant) {
      case 'link': {
        return 'underline';
      }

      // case "solid"
      // case "link"
      default: {
        return 'none';
      }
    }
  }};

  font-size: ${({ size }) => {
    switch (size) {
      case 'xsmall': {
        return '14px';
      }
      case 'small': {
        return '16px';
      }
      default: {
        return '20px';
      }
    }
  }};

  font-family: 'Lato', sans-serif;
  font-weight: normal;
  display: flex;
  justify-content: center;
  align-items: center;

  gap: ${({ size }) => {
    switch (size) {
      case 'xsmall': {
        return '4px';
      }
      case 'small': {
        return '6px';
      }
      default: {
        return '10px';
      }
    }
  }};

  padding: 0 1rem;

  &[disabled] {
    opacity: 0.6;
  }

  & > svg {
    font-size: inherit;
  }

  transition: 0.3s background ease-in;

  &:hover {
    background: ${({ variant }) => {
      switch (variant) {
        case 'transparent': {
          return 'rgba(0, 0, 0, .2)';
        }

        default: {
          return 'var(--background)';
        }
      }
    }};
  }

  & + & {
    border-left: 1px solid currentColor;
  }
`;

const rotate = keyframes`
  to {
    transform: rotate(1turn);
  }
`;

export const Rotate = styled('span')`
  animation: ${rotate} 1s linear infinite;
`;
